import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, EventEmitter, inject, Input, OnChanges, Output } from '@angular/core';
import { Router, RouterModule } from '@angular/router';
import { LocaleSharedModule } from '@app/i18n/locale-shared.module';

import { AuthorInfoComponent } from '@core/components/author-info/author-info.component';
import {
  AccountService,
  ClassroomsService,
  FeaturesService,
  FilesService,
  UserServiceContractsService,
} from '@core/services';
import { ChangableComponent } from '@models/changable.component';
import { Store } from '@ngrx/store';
import { IconComponent } from '@shared/icon/icon.component';
import { createPin, removePin } from '@store/actions/profile.actions';
import {
  AccessStatusEnum,
  AnyType,
  BreakUserServiceContractRequest,
  Classroom,
  ClassroomParticipant,
  EContentPanelRoute,
  EntityTypeEnum,
  FeatureEnum,
  ImageSizeEnum,
  Meeting,
  PinnedEntity,
  User,
  UserServiceContract,
} from 'lingo2-models';
import { ClassroomTypeEnum } from 'lingo2-models/dist/content/classroom';
import { EUserServiceType } from 'lingo2-models/dist/content/user-service/user-service-type-enum';
import { FormatPipeModule } from 'lingo2-ngx-date-fns';
import { isBoolean } from 'lodash';
import { DeviceDetectorService } from 'ngx-device-detector';
import { OnUiButtonModule, OnUiCardMenuModule, OnUiCoverModule } from 'onclass-ui';
import { OnUiCover } from 'onclass-ui/lib/modules/on-ui-cover/on-ui-cover.component';
import { of } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { InviteToClassroomModule } from '../../../../classrooms/_components/invite-to-classroom/invite-to-classroom.module';
import { ClassroomMeetingFormWrapperModule } from '../../../../classrooms/_components/meeting-form-wrapper/meeting-form-wrapper.module';
import { AlertModule } from '../../../../core/components/alert/alert.module';
import { MeetingsTitleModule } from '../../../../core/components/meeting-title/meetings-title.module';
import { MobileModalModule } from '../../../../core/components/mobile-modal/mobile-modal.module';
import { ModalModule } from '../../../../core/components/modal/modal.module';
import { i18nService as TranslateService } from '../../../../i18n/translate.service';
import { BreakContractReasonData, classBreakReasonsData, menuOptions } from '../constants';
import { ClassroomCardActionsEnum, IClassroomCardOption } from '../models';

@Component({
  selector: 'app-classroom-mini',
  standalone: true,
  imports: [
    LocaleSharedModule,
    CommonModule,

    OnUiCoverModule,
    OnUiButtonModule,
    OnUiCardMenuModule,
    AlertModule,
    ModalModule,
    InviteToClassroomModule,
    MobileModalModule,
    ClassroomMeetingFormWrapperModule,
    IconComponent,
    RouterModule,
    MeetingsTitleModule,
    FormatPipeModule,
    AuthorInfoComponent,
  ],
  templateUrl: './classroom-mini.component.html',
  styleUrls: ['./classroom-mini.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ClassroomMiniComponent extends ChangableComponent implements OnChanges {
  private store = inject(Store);
  private router = inject(Router);
  deviceService = inject(DeviceDetectorService);
  private translate = inject(TranslateService);
  private classroomsService = inject(ClassroomsService);
  private contractsService = inject(UserServiceContractsService);
  private accountService = inject(AccountService);
  private features = inject(FeaturesService);

  @Input()
  public classroom: Classroom;

  @Input()
  public pins: PinnedEntity[];

  @Input()
  public display_mode: string;

  @Input()
  public me: User;

  @Output()
  public edit = new EventEmitter<Classroom>();

  @Output()
  public update = new EventEmitter<boolean>();

  public classroomRoute = ClassroomsService.classroomRoute;
  public classroomCardActionsEnum = ClassroomCardActionsEnum;
  public meetingModal: boolean;
  public inviteModal: boolean;
  public readonly route = '/classrooms';
  public user: User;
  public profileUrl: string[];
  public fullName: string;
  public classroomUrl: string;
  public cover: OnUiCover;
  public avatar: OnUiCover;
  public options = menuOptions;

  public classBreakAlertShow = false;
  public classBreakReasons: 'no_classes' | 'left_classes' | 'less_24_hours' = 'no_classes';
  public alertData: BreakContractReasonData = null;

  public ngOnChanges(): void {
    // const pinnedEntityIds = (this.pins || []).map((x) => x.entity_id);
    if (this.classroom) {
      // this.classroom.visit_info = {
      //   ...this.classroom.visit_info,
      //   is_favorite: pinnedEntityIds.includes(this.classroom.id),
      // };

      this.prepareCard();
    }
  }

  public get contract(): UserServiceContract {
    return this.classroom.user_service_contract;
  }

  public get isPined(): boolean {
    const pinnedEntityIds = (this.pins || []).map((x) => x.entity_id);
    return !!this.classroom?.id && pinnedEntityIds.includes(this.classroom?.id);
  }

  public get isPrivate(): boolean {
    return this.classroom?.access === AccessStatusEnum.private;
  }

  public get meetingsCount(): number {
    return (this.classroom?.stats?.meeting_planned || 0) + (this.classroom?.stats?.meeting_finished || 0);
  }

  public get participantsCount(): number {
    return this.classroom?.stats?.accepted || 0;
  }

  public isEnabledMenuOption(option: IClassroomCardOption): boolean {
    if (!('enabled' in option)) {
      return true;
    }
    if (isBoolean(option.enabled)) {
      return Boolean(option.enabled);
    }

    if (option.id === ClassroomCardActionsEnum.pin || option.id === ClassroomCardActionsEnum.unpin) {
      return false;
    }

    return (option.enabled as AnyType)(this.classroom, { is_pined: this.isPined });
  }

  public onAction(option: IClassroomCardOption): void {
    if (!this.isEnabledMenuOption(option)) {
      return;
    }

    switch (option.id) {
      case ClassroomCardActionsEnum.schedule_meeting:
        return this.openMeetingModal();

      case ClassroomCardActionsEnum.invite:
        return this.openInviteModal();

      case ClassroomCardActionsEnum.pin:
        return this.pinClassroom();

      case ClassroomCardActionsEnum.unpin:
        return this.unpinClassroom();

      case ClassroomCardActionsEnum.leave:
        return this.leaveClassroom();

      case ClassroomCardActionsEnum.break_contract:
        return this.onContractBreak();

      case ClassroomCardActionsEnum.archive:
        return this.archiveClassroom();

      case ClassroomCardActionsEnum.edit:
        this.edit.emit(this.classroom);
        break;

      case ClassroomCardActionsEnum.restore:
        return this.restoreClassroom();

      default:
        const _id = ClassroomCardActionsEnum[option.id] || '?';
        alert(`Action ${_id} not supported`);
        break;
    }
  }

  public togglePined() {
    return this.isPined ? this.unpinClassroom() : this.pinClassroom();
  }

  public pinClassroom(): void {
    const pinedEntity = (this.pins || []).find((p) => p.entity_id === this.classroom.id);
    if (!pinedEntity) {
      const pin: Partial<PinnedEntity> = {
        type: EntityTypeEnum.classroom,
        entity_id: this.classroom.id,
        title: this.classroom.title,
        image_id: this.classroom.cover_id,
      };
      this.store.dispatch(createPin({ pin }));
    }
  }

  private unpinClassroom() {
    const pinedEntity = (this.pins || []).find((p) => p.entity_id === this.classroom.id);
    if (pinedEntity) {
      this.store.dispatch(removePin({ pin_id: pinedEntity.id }));
    }
  }

  private archiveClassroom(): void {
    this.classroomsService
      .deleteClassroom(this.classroom.id)
      .pipe(takeUntil(this.destroyed$))
      .subscribe({
        next: (res) => {
          this.update.emit(true);
          this.detectChanges();
        },
        error: (error) => {
          this.errorNotificationService.captureError(error, 'LOAD-SOMEDATA');
        },
      });
  }

  private restoreClassroom(): void {
    this.classroomsService
      .restoreClassroom(this.classroom.id)
      .pipe(takeUntil(this.destroyed$))
      .subscribe({
        next: (res) => {
          this.update.emit(true);
          this.detectChanges();
        },
        error: (error) => {
          this.errorNotificationService.captureError(error, 'LOAD-SOMEDATA');
        },
      });
  }

  private leaveClassroom(): void {
    this.unpinClassroom();

    this.classroomsService
      .leaveClassroom(this.classroom.id)
      .pipe(takeUntil(this.destroyed$))
      .subscribe({
        next: () => {
          this.update.emit(true);
          this.detectChanges();
        },
        error: (error) => {
          this.errorNotificationService.captureError(error, 'LOAD-SOMEDATA');
        },
      });
  }

  private onContractBreak() {
    // @TODO: Сделать вычисление по какой причине показывается алерт
    this.classBreakReasons = 'no_classes';

    if (this.classroom.user_service) {
      if (this.classroom.user_service.type === EUserServiceType.regular) {
        this.alertData = classBreakReasonsData['regular'][this.classBreakReasons];
      } else if (
        this.classroom.user_service.type === EUserServiceType.course ||
        this.classroom.user_service.type === EUserServiceType.mini
      ) {
        this.alertData = classBreakReasonsData['course'][this.classBreakReasons];
      } else {
        this.alertData = null;
      }
    } else {
      this.alertData = null;
    }

    this.classBreakAlertShow = true;
    this.detectChanges();
  }

  public breakUserServiceContract() {
    if (!this.classroom.user_service_contract_id) {
      return;
    }

    this.unpinClassroom();

    const request = new BreakUserServiceContractRequest({
      user_service_contract_id: this.classroom.user_service_contract_id,
      reason: null, // TODO
      comment: null, // TODO
    });
    this.contractsService
      .breakContract(request)
      .pipe(takeUntil(this.destroyed$))
      .subscribe({
        next: () => {
          this.classBreakAlertShow = false;
          this.detectChanges();
          this.router.navigate([EContentPanelRoute['my-classes/classrooms']]).then(() => true);
        },
        error: (error) => {
          this.errorNotificationService.captureError(error, 'LOAD-SOMEDATA');
        },
      });
  }

  public openInviteModal() {
    this.inviteModal = true;
    this.detectChanges();
  }

  public closeInviteModal() {
    this.inviteModal = false;
    this.detectChanges();
  }

  public openMeetingModal() {
    this.meetingModal = true;
    this.detectChanges();
  }

  public closeMeetingModal() {
    this.meetingModal = false;
    this.detectChanges();
  }

  public classroomTypeLabel(): string {
    switch (this.classroom?.type) {
      case ClassroomTypeEnum.individual:
        return 'Individual';
      case ClassroomTypeEnum.group:
        return 'Group';
      default:
        return '';
    }
  }

  private loadAuthorDetails(id: string) {
    if (!id) {
      return;
    }
    this.accountService
      .getUserById(id, 'LOAD_TIME')
      .pipe(takeUntil(this.destroyed$))
      .subscribe({
        next: (user) => {
          this.user = user;
          this.profileUrl = AccountService.accountRoute(user);
          this.fullName = AccountService.getUserFullName(user);
          this.avatar = {
            img: FilesService.getFileUrlBySize(user?.userpic_id, ImageSizeEnum.sm),
            form: 'circle',
            type: 'avatar',
          };
          this.detectChanges();
        },
        error: (error) => {
          this.errorNotificationService.captureError(error, 'LOAD-SOMEDATA');
        },
      });
  }

  private prepareCard() {
    this.loadAuthorDetails(this.classroom.author_id);
    this.classroomUrl = ClassroomsService.classroomRoute(this.classroom).join('/');
    this.cover = {
      img:
        FilesService.getFileBySize(this.classroom.cover, [ImageSizeEnum.wide, ImageSizeEnum.md])?.url ||
        FilesService.getFileUrlBySize(this.classroom.cover_id, ImageSizeEnum.wide),
      form: 'base',
      type: 'cover',
      aspect: '16/9',
      link: this.classroomUrl,
    };
    this.detectChanges();
  }

  public get moreParticipantsCount(): number {
    return this.classroom?.stats?.accepted - this.filteredParticipants.length;
  }

  public isPinAvailable() {
    return this.features.isAvailable(FeatureEnum.pins);
  }

  public get filteredParticipants(): ClassroomParticipant[] {
    return this.classroom.participants.filter((el) => el.user_id !== this.me.id);
  }

  public avatarParticipant(participant: User): OnUiCover {
    return {
      type: 'avatar',
      img: FilesService.getFileUrlBySize(participant?.userpic_id, ImageSizeEnum.sm),
      title: AccountService.getUserFullName(participant),
      form: 'circle',
    };
  }

  public participantsTrackByFn(index, participant: ClassroomParticipant): string {
    return participant.user_id;
  }

  public loadParticipant$(id: string) {
    if (!id) {
      return of(null);
    }
    return this.accountService.getUserById(id, 'LOAD_TIME');
  }

  public trackByFn(index) {
    return index;
  }

  public meetingTitle(meeting: Meeting) {
    if (!meeting) {
      return null;
    }

    const titleParts = [meeting.title];

    let subject = '';
    if (meeting.subject) {
      subject = (meeting.subject ? meeting.subject.title : null) || '---';
    }
    const level = meeting.level ? meeting.level.title : this.translate.directInstant('subject-levels.any-level');
    const authorName = AccountService.getUserFullName(meeting.author);

    if (subject) {
      titleParts.push(', ' + subject);
    }
    if (level) {
      titleParts.push(', ', level);
    }
    if (authorName) {
      titleParts.push(', ', authorName);
    }
    return titleParts.join('');
  }
}
